import airair from "@/public/images/rate/airair.png";
import best from "@/public/images/rate/best.png";
import complex from "@/public/images/rate/complex.png";
import dg from "@/public/images/rate/dg.png";
import direct from "@/public/images/rate/direct.png";
import express from "@/public/images/rate/express.png";
import temperature from "@/public/images/rate/temperature.png";
import { CComboboxOption } from "./components/cCombobox/CCombobox";
import { HeaderFilterItem } from "./components/crudTable/type";
import { ImageCheckboxOption } from "./components/form/FormImageCheckboxes";
import { RadioItem } from "./components/form/FormRadioGroup";
import { DayString, TFunction } from "./type";
import { CheckboxOption, SelectOption } from "./types/types";

export const appName = process.env.APP_NAME || "Cargogo Online";

export { getJwtSecret } from "@/src/util/jwt-secret";

export const port = process.env.PORT || 3000;

console.log(`[DEBUG] gRPC Base URL: ${process.env.NEXT_PUBLIC_GRPC_BASE_URL}`);

export const gRPCBaseURL = process.env.NEXT_PUBLIC_GRPC_BASE_URL || "/backend-api";

const getGRPCServerURL = () => {
  const envServerUrl = process.env.GRPC_SERVER_URL;
  if (envServerUrl) return envServerUrl;

  const publicUrl = process.env.NEXT_PUBLIC_GRPC_BASE_URL;
  if (publicUrl && publicUrl.startsWith("http")) return publicUrl;

  return "https://uat.cargogo.com";
};

export const gRPCServerURL = getGRPCServerURL();

export const fileBaseURL = "/";

export type Locale = "en-US" | "zh-CN" | "zh-TW";

export const locale: Locale = "en-US";

export const phoneCountryCode = "852";

export const dateFormat = "DD/MM/YYYY";

export const pageSize = 50;

export const rowsPerPageOptions = [10, 20, 50, 100];

export const locales: {
  shortName: string;
  name: string;
  value: Locale;
  image: string;
}[] = [
  { shortName: "EN", name: "English", value: "en-US", image: "/flags/usa.svg" },
  {
    shortName: "繁",
    name: "繁體中文",
    value: "zh-TW",
    image: "/flags/hongkong.svg",
  },
  {
    shortName: "简",
    name: "简体中文",
    value: "zh-CN",
    image: "/flags/china.svg",
  },
];

export const publicRoutesRegex = /^(\/login|\/register|\/forgotpassword|\/reset-password)$/;

export { protectedRoutesRegex } from "@/src/util/route-permissions";

export const ROLE_SUPER_ADMIN = "Super Admin";
export const ROLE_SUPER_USER = "Super User";

export const UNAUTHORIZED = "unauthorized";
export const TOKEN_EXPIRED = "token_expired";

export const Events = {
  Logout: "auth:logout",
  InvalidToken: "auth:invalid_token",
};

export const days: ReadonlyArray<{ day: DayString; value: number }> = [
  { day: "mon", value: 1 },
  { day: "tue", value: 2 },
  { day: "wed", value: 3 },
  { day: "thu", value: 4 },
  { day: "fri", value: 5 },
  { day: "sat", value: 6 },
  { day: "sun", value: 7 },
] as const;

export const freightSettingsRegionSettings = [
  {
    region: "North America",
    dimensionalFactor: undefined,
    sellingVolShare: { numerator: undefined, denominator: undefined },
    costVolShare: { numerator: undefined, denominator: undefined },
  },
  {
    region: "South America",
    dimensionalFactor: undefined,
    sellingVolShare: { numerator: undefined, denominator: undefined },
    costVolShare: { numerator: undefined, denominator: undefined },
  },
  {
    region: "EU",
    dimensionalFactor: undefined,
    sellingVolShare: { numerator: undefined, denominator: undefined },
    costVolShare: { numerator: undefined, denominator: undefined },
  },
  {
    region: "Asia",
    dimensionalFactor: undefined,
    sellingVolShare: { numerator: undefined, denominator: undefined },
    costVolShare: { numerator: undefined, denominator: undefined },
  },
  {
    region: "Africa",
    dimensionalFactor: undefined,
    sellingVolShare: { numerator: undefined, denominator: undefined },
    costVolShare: { numerator: undefined, denominator: undefined },
  },
  {
    region: "India",
    dimensionalFactor: undefined,
    sellingVolShare: { numerator: undefined, denominator: undefined },
    costVolShare: { numerator: undefined, denominator: undefined },
  },
  {
    region: "Oceania",
    dimensionalFactor: undefined,
    sellingVolShare: { numerator: undefined, denominator: undefined },
    costVolShare: { numerator: undefined, denominator: undefined },
  },
];

export const freightSettingsServices = [
  {
    service: "Pick Up",
    cost: undefined,
    price: undefined,
  },
  {
    service: "X-Ray",
    cost: undefined,
    price: undefined,
  },
];

export const freightCostTransportModeCheckboxOptions: CheckboxOption[] = [
  { value: "AIR_AIR", label: "database.transport_mode_options.air_air" },
  { value: "AIR_TRUCK", label: "database.transport_mode_options.air_truck" },
  { value: "DIRECT", label: "database.transport_mode_options.direct" },
  { value: "FREIGHTER", label: "database.transport_mode_options.freighter" },
  { value: "PAX", label: "database.transport_mode_options.pax" },
  { value: "ROAD", label: "database.transport_mode_options.road" },
  { value: "TRUCK", label: "database.transport_mode_options.truck" },
  { value: "RAIL", label: "database.transport_mode_options.rail" },
  { value: "W747_7WIDE", label: "database.transport_mode_options.w747_7wide" },
  { value: "W787", label: "database.transport_mode_options.w787" },
  { value: "CROSS_BORDER", label: "database.transport_mode_options.cross_border" },
  { value: "A380", label: "database.transport_mode_options.a380" },
  { value: "A330", label: "database.transport_mode_options.a330" },
  { value: "A320", label: "database.transport_mode_options.a320" },
  { value: "VAN", label: "database.transport_mode_options.van" },
  { value: "CONTAINER_TRUCK", label: "database.transport_mode_options.container_truck" },
  { value: "LORRY", label: "database.transport_mode_options.lorry" },
];

export const freightCostCargoTypes: RadioItem[] = [
  { value: "GENERAL_CARGO", label: "orders.cargo_type_options.general_cargo" },
  { value: "SPECIAL_CARGO", label: "orders.cargo_type_options.special_cargo" },
];

export const freightCostProducts = [
  { id: "airtoair", name: "Air/Air Transit", type: "general_cargoes" },
  { id: "complex", name: "Air/Truck Complex Transit", type: "general_cargoes" },
  { id: "dggoods", name: "DG Goods", type: "special_services" },
  { id: "temperature", name: "Temperature Control", type: "special_services" },
  { id: "express", name: "Express", type: "special_services" },
];

export const adjustTypeOptions: CComboboxOption[] = [
  { value: "Amount", label: "common.amount" },
  { value: "Rate", label: "common.plus_minus_rate" },
];

export const weekdayOptions: CComboboxOption[] = [
  { value: "mon", label: "weekdays.monday" },
  { value: "tue", label: "weekdays.tuesday" },
  { value: "wed", label: "weekdays.wednesday" },
  { value: "thu", label: "weekdays.thursday" },
  { value: "fri", label: "weekdays.friday" },
  { value: "sat", label: "weekdays.saturday" },
  { value: "sun", label: "weekdays.sunday" },
];

export const freightCostTransportModeOptions: CComboboxOption[] = [
  { value: "AIR_AIR", label: "Air / Air" },
  { value: "AIR_TRUCK", label: "Air / Truck" },
  { value: "DIRECT", label: "Direct" },
  { value: "FREIGHTER", label: "Freighter" },
  { value: "PAX", label: "PAX" },
  { value: "ROAD", label: "Road" },
  { value: "TRUCK", label: "Truck" },
  { value: "RAIL", label: "Rail" },
  { value: "W747_7WIDE", label: "747-7Wide" },
  { value: "W787", label: "787" },
  { value: "CROSS_BORDER", label: "Cross Border" },
  { value: "A380", label: "A380" },
  { value: "A330", label: "A330" },
  { value: "A320", label: "A320" },
  { value: "VAN", label: "Van" },
  { value: "CONTAINER_TRUCK", label: "Continer Truck" },
  { value: "LORRY", label: "Lorry" },
];

export const freightCostMoreServiceOptions: CComboboxOption[] = [
  { value: "FRE_FRESH", label: "FRE - Fresh" },
  { value: "LIV_LIVE_ANIMAL", label: "LIV - Live Animal" },
  { value: "GEN_GENERAL_CARGO", label: "GEN - General Cargo" },
  { value: "DGD_DANGEROUS_GOODS", label: "DGD - Dangerous Goods" },
  { value: "EXP_EXPRESS", label: "EXP - Express" },
  { value: "COU_COURIER", label: "COU - Courier" },
  { value: "TEMP_CONTROL", label: "Temp Control / 溫控保溫" },
];

export const freightCostCapacityManageOptions: CComboboxOption[] = [
  { value: "AIR_CO_LOAD", label: "Co-load" },
  { value: "AIR_CONSOLD_HAWB", label: "Consolidation" },
  { value: "AIR_DIRECT_AWB", label: "Direct AWB" },
  { value: "AIR_BULK", label: "Bulk" },
  { value: "AIR_BUP", label: "BUP" },
];

export const airBillingCapacityManageOptions = freightCostCapacityManageOptions;

export const freightSettingsApplyShippingOptions = freightCostCapacityManageOptions.map(({ value }) => ({
  enabled: false,
  type: value,
  ratio: { numerator: undefined, denominator: undefined },
}));

export const freightSettingsApplyDimensionalFactor = {
  enabled: false,
  dimensionalFactor: undefined as number | undefined,
};

export const reservationProducts = [
  { id: "dggoods", name: "orders.dggoods", type: "products" },
  { id: "temperature", name: "orders.temperature", type: "products" },
  { id: "express", name: "orders.express", type: "products" },
  { id: "airtoair", name: "orders.air_air_transit", type: "products" },
];

export const tableServiceFilters: HeaderFilterItem[] = [
  { id: "price", name: `common.price` },
  {
    id: "operate_system",
    name: `common.operate_system`,
  },
];

export const tableAdminStatusFilters: HeaderFilterItem[] = [
  {
    id: "ACTIVE",
    name: "active",
  },
  {
    id: "INACTIVE",
    name: "inactive",
  },
];

export const cargoGoServiceOptions = (t: TFunction) => {
  return [
    { value: "price", label: `CargoGo ${t("common.price")}` },
    { value: "operate", label: `CargoGo ${t("common.operate_system")}` },
  ] as RadioItem[];
};

export const currencyOptions: CComboboxOption[] = [
  { value: "HKD", label: "HKD" },
  { value: "USD", label: "USD" },
  { value: "CNY", label: "CNY" },
  { value: "EUR", label: "EUR" },
];

export const freightRateRoutingOptions: CComboboxOption[] = [
  { value: "0", label: "EK" },
  { value: "1", label: "CX" },
  { value: "2", label: "SQ" },
  { value: "3", label: "LH" },
];

export const aircraftTypeOptions: CComboboxOption[] = [
  { value: "0", label: "Freighter" },
  { value: "1", label: "PAX" },
  { value: "2", label: "Track" },
];

export const contourOptions: CComboboxOption[] = [
  { value: "AKE", label: "AKE" },
  { value: "AKC", label: "AKC" },
  { value: "AKH", label: "AKH" },
  { value: "AKN", label: "AKN" },
  { value: "AMA", label: "AMA" },
  { value: "AMJ", label: "AMJ" },
  { value: "AKE_AKC", label: "AKE+AKC" },
  { value: "AKH_AKN", label: "AKH+AKN" },
  { value: "PMC", label: "PMC" },
  { value: "PLA", label: "PLA" },
  { value: "PAG", label: "PAG" },
  { value: "RAP", label: "RAP" },
  { value: "RKN", label: "RKN" },
];

export const freightCostProductOptions: CheckboxOption[] = freightCostProducts.map((p) => ({
  value: p.id,
  label: p.name,
}));

export const freightCostGeneralCargoOptions: CheckboxOption[] = [
  { value: "0", label: "Air/Air Transit" },
  { value: "1", label: "Air/Truck Complex Transit" },
];

export const freightCostSpecialServiceOptions: CheckboxOption[] = [
  { value: "0", label: "DG Goods" },
  { value: "1", label: "Temperature Control" },
];

export const applyBusinessOptions: CheckboxOption[] = [
  { label: "common.air_export", value: "airExport", checked: false },
  { label: "common.air_import", value: "airImport", checked: false },
  { label: "common.sea_export", value: "seaExport", checked: false },
  { label: "common.sea_import", value: "seaImport", checked: false },
  { label: "common.warehouse", value: "warehouse", checked: false },
  { label: "common.transportation", value: "transportation", checked: false },
];

export const freightContractStatusOptions: CComboboxOption[] = [
  { value: "ACTIVE", label: "database.freight_contract_status.active" },
  { value: "HOLD", label: "database.freight_contract_status.hold" },
  { value: "TERMINATE", label: "database.freight_contract_status.terminate" },
];

export const userStatusOptions: CComboboxOption[] = [
  { value: "INACTIVE", label: "common.inactive" },
  { value: "ACTIVE", label: "common.active" },
];

// export const surchargeShippingOptions: CComboboxOption[] = [
//   { value: "1", label: "TEST 1" },
//   { value: "2", label: "TEST 2" },
//   { value: "3", label: "TEST 3" },
//   { value: "4", label: "TEST 4" },
//   { value: "5", label: "TEST 5" },
// ];

export const airlineOptions: CComboboxOption[] = [
  { value: "CX", label: "CX" },
  { value: "KA", label: "KA" },
  { value: "CZ", label: "CZ" },
  { value: "MU", label: "MU" },
  { value: "CA", label: "CA" },
  { value: "EK", label: "EK" },
  { value: "SQ", label: "SQ" },
  { value: "TG", label: "TG" },
  { value: "JL", label: "JL" },
  { value: "NH", label: "NH" },
  { value: "KE", label: "KE" },
  { value: "OZ", label: "OZ" },
  { value: "BR", label: "BR" },
  { value: "CI", label: "CI" },
  { value: "QF", label: "QF" },
  { value: "AA", label: "AA" },
  { value: "UA", label: "UA" },
  { value: "DL", label: "DL" },
  { value: "LH", label: "LH" },
  { value: "AF", label: "AF" },
];

export const stateOptions: CComboboxOption[] = [
  { value: "US", label: "US" },
  { value: "CA", label: "CA" },
  { value: "UK", label: "UK" },
  { value: "AU", label: "AU" },
  { value: "CN", label: "CN" },
  { value: "JP", label: "JP" },
  { value: "KR", label: "KR" },
  { value: "SG", label: "SG" },
  { value: "HK", label: "HK" },
  { value: "TW", label: "TW" },
];

export const capacityManageOptions: CComboboxOption[] = [
  { value: "AIR", label: "Air" },
  { value: "AIR-CONSOL", label: "Air - Consol" },
  { value: "AIR-BULK", label: "Air - Bulk" },
  { value: "AIR-BUP", label: "Air - BUP" },
  { value: "AIR-CO-LOAD", label: "Air - Co-load" },
  { value: "SEA", label: "Sea" },
  { value: "LAND", label: "Land" },
];

export const freightCostAdjustmentTypeOptions: CComboboxOption[] = [
  { value: "rate", label: "admin.adjust_types.rate" },
  { value: "amount", label: "admin.adjust_types.amount" },
];

export const shippingOptions: CComboboxOption[] = [
  { value: "1", label: "Standard" },
  { value: "2", label: "International" },
  { value: "3", label: "Freight" },
];

export const zoneOptions: CComboboxOption[] = [
  { value: "ZONE_UNSPECIFIED", label: "Select Zone" },
  { value: "NA", label: "North America" },
  { value: "SA", label: "South America" },
  { value: "EU", label: "EU" },
  { value: "AS", label: "Asia" },
  { value: "AFR", label: "Africa" },
  { value: "IND", label: "India" },
  { value: "OC", label: "Oceania" },
  { value: "ME", label: "Middle East" },
];

export const destZoneOptions: SelectOption[] = [
  { id: "ZONE_UNSPECIFIED", value: "Select Zone" },
  { id: "NA", value: "North America" },
  { id: "SA", value: "South America" },
  { id: "EU", value: "EU" },
  { id: "AS", value: "Asia" },
  { id: "AFR", value: "Africa" },
  { id: "IND", value: "India" },
  { id: "OC", value: "Oceania" },
];

export const supplierOptions: CComboboxOption[] = [
  { value: "0", label: "Supplier 1" },
  { value: "1", label: "Supplier 2" },
  { value: "2", label: "Supplier 3" },
  { value: "3", label: "Supplier 4" },
  { value: "4", label: "Supplier 5" },
  { value: "5", label: "Supplier 6" },
  { value: "6", label: "Supplier 7" },
];

export const salesmanOptions: CComboboxOption[] = [
  { value: "0", label: "Salesman 1" },
  { value: "1", label: "Salesman 2" },
  { value: "2", label: "Salesman 3" },
  { value: "3", label: "Salesman 4" },
  { value: "4", label: "Salesman 5" },
  { value: "5", label: "Salesman 6" },
  { value: "6", label: "Salesman 7" },
];

export const rateGeneralProductOptions: ImageCheckboxOption[] = [
  {
    icon: best,
    label: "rate.gargogo_hot_picks",
    value: "best",
  },
  {
    icon: airair,
    label: "rate.air_air_transit",
    value: "airair",
  },
  {
    icon: direct,
    label: "rate.direct_fly",
    value: "direct",
  },
  {
    icon: complex,
    label: "rate.air_truck_complex_transit",
    value: "complex",
  },
];

export const rateSpecialProductOptions: ImageCheckboxOption[] = [
  {
    icon: dg,
    label: "rate.dg_goods",
    value: "dg",
  },
  {
    icon: temperature,
    label: "rate.temp_control",
    value: "temperature",
  },
  {
    icon: express,
    label: "rate.express",
    value: "express",
  },
];

export const variationTypeOptions: CComboboxOption[] = [
  { value: "VAR_SHIP", label: "admin.embargo_shipper_consignee" },
  { value: "VAR_COMM", label: "admin.embargo_commodity" },
  { value: "VAR_COM", label: "admin.commodity" },
  { value: "VAR_REQ", label: "admin.require" },
  { value: "VAR_CTL", label: "admin.control_list" },
  { value: "VAR_WCH", label: "admin.watching_list" },
  { value: "VAR_MAX", label: "admin.max_size" },
];

export const contentTypeCodeOptions: CComboboxOption[] = [
  { value: "DOC_AWB_DEC", label: "admin.doc_awb_declare" },
  { value: "DOC_BAT", label: "admin.doc_battery_letter" },
  { value: "DOC_MSDS", label: "admin.doc_msds" },
  { value: "DOC_MAG", label: "admin.doc_magnet_letter" },
  { value: "DOC_DIR_AWB", label: "admin.doc_direct_awb_only" },
  { value: "DOC_INV", label: "admin.doc_commercial_invoice" },
  { value: "DOC_PKG", label: "admin.doc_packing_list" },
  { value: "ACT_CHK", label: "admin.action_cargo_check" },
  { value: "ACT_APP", label: "admin.action_doc_approval" },
  { value: "ACT_SNC", label: "admin.action_sanction" },
];
